import type { ServiceConfig, Size, SlotBlueprint } from '@/lib/types'

export function emptyBlueprint(): SlotBlueprint {
  return {
    slot: 'NEW_SLOT',
    sizes: { desktop: [], tablet: [], mobile: [] },
    adHigh: { desktop: 0, tablet: 0, mobile: 0 },
    gamOnly: false,
    bids: [],
  }
}

// ── Walidacja slotów ─────────────────────────────────────────────────────────
// Sloty z tej zakładki są ŹRÓDŁEM PRAWDY dla aukcji Prebid po stronie serwisu
// (swnext). Z każdego slotu konsument buduje:
//   code = `${gpidAccount || service}/${slot}`
//   gpid = `/1042598/${gpidNetwork}/${gpidAccount}//${slot}`
//   bids = slot.bids scalone z rejestrem `bidders`
// Dlatego `slot` MUSI być gołą nazwą slotu z GAM (np. „HS_DM_HH_XL1"), bez
// prefiksu serwisu — inaczej code/gpid nie dopasują się do slotów na stronie
// i aukcja nie zwróci żadnych bidów.

export interface BlueprintIssues {
  emptySizes: boolean
  zeroHeight: boolean
  prefixed: boolean
  noBids: boolean
  unknownBidders: string[]
}

export function dedupeSizes(sizes: Size[]): Size[] {
  const seen = new Set<string>()
  const out: Size[] = []
  for (const s of sizes) {
    const key = `${s[0]}x${s[1]}`
    if (!seen.has(key)) {
      seen.add(key)
      out.push(s)
    }
  }
  return out
}

export function blueprintIssues(bp: SlotBlueprint, draft: ServiceConfig): BlueprintIssues {
  const prefixes = [draft.gpidAccount, draft.service].filter(Boolean)
  const prefixed = prefixes.some((p) => bp.slot.startsWith(p + '_'))
  const registry = draft.bidders ?? {}
  const bids = bp.bids ?? []

  // Slot GAM-only nie licytuje — brak bidów i rozmiarów jest dla niego normą.
  if (bp.gamOnly) {
    return { emptySizes: false, zeroHeight: false, prefixed, noBids: false, unknownBidders: [] }
  }

  return {
    emptySizes:
      bp.sizes.desktop.length === 0 && bp.sizes.tablet.length === 0 && bp.sizes.mobile.length === 0,
    zeroHeight: bp.adHigh.desktop === 0 && bp.adHigh.tablet === 0 && bp.adHigh.mobile === 0,
    prefixed,
    noBids: bids.length === 0,
    // Bidder spoza rejestru nie dostanie wspólnych parametrów (np. rtbhouse bez
    // publisherId) — efektem jest cichy noBid, nie błąd.
    unknownBidders: bids.map((b) => b.bidder).filter((b) => b && !(b in registry)),
  }
}

export function hasBlueprintIssues(i: BlueprintIssues): boolean {
  return i.emptySizes || i.zeroHeight || i.prefixed || i.noBids || i.unknownBidders.length > 0
}

export function blueprintIssueLabels(i: BlueprintIssues): string[] {
  return [
    i.prefixed && 'podwójny prefiks serwisu → zły code, brak bidów',
    i.emptySizes && 'brak rozmiarów → brak adUnitu',
    i.zeroHeight && 'adHigh = 0 → kontener zwinie się do 0px',
    i.noBids && 'brak bidów → slot nie licytuje',
    i.unknownBidders.length > 0 &&
      `dostawcy spoza rejestru: ${i.unknownBidders.join(', ')} (zakładka „Dostawcy")`,
  ].filter((x): x is string => Boolean(x))
}
